import { Tooltip } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import { Fragment, useEffect, useState } from 'react';

import { DataBuilderTrackingEvents } from '@revelio/core';
import { Button, Option, Text } from '@revelio/ui';

import { useDatasetStorage } from '../api/useDatasetStorage';
import { DataBuilderGrid } from '../components/ui/DataBuilderGrid';
import { DataBuilderOption } from '../components/ui/DataBuilderOption';
import { useDataBuilderNavigation } from '../useDataBuilderNavigation';
import styles from './step0-choose.module.css';

type DatasetStartMode = 'new' | 'saved';

type StartOption = {
  id: DatasetStartMode;
  title: string;
  detail: string;
  testId: string;
};

const START_OPTIONS: StartOption[] = [
  {
    id: 'new',
    title: 'New Datasets',
    detail: 'Build datasets from scratch using your selected sources',
    testId: 'step0-new-dataset',
  },
  {
    id: 'saved',
    title: 'Saved Datasets',
    detail: "Resume or modify existing datasets you've already created",
    testId: 'step0-saved-datasets',
  },
];

const NO_SAVED_DATASETS_TOOLTIP = "You don't have any saved datasets yet";

export const DataBuilderChooseStep = () => {
  const [selectedMode, setSelectedMode] = useState<DatasetStartMode>('new');
  const { goToDatasetStep, goToSavedDatasetsStep } = useDataBuilderNavigation();
  const { listDatasets, datasetsQueryKey } = useDatasetStorage();

  const { data: savedDatasets, isSuccess: hasLoadedSavedDatasets } = useQuery({
    queryKey: datasetsQueryKey,
    queryFn: listDatasets,
  });

  const isSavedDisabled =
    hasLoadedSavedDatasets &&
    savedDatasets !== undefined &&
    savedDatasets.length === 0;

  useEffect(() => {
    if (isSavedDisabled && selectedMode === 'saved') {
      setSelectedMode('new');
    }
  }, [isSavedDisabled, selectedMode]);

  const handleContinue = () => {
    mixpanel.track(DataBuilderTrackingEvents.STARTED, {
      entry_mode: selectedMode,
      page: 'data builder',
      page_type: 'databuilder_flow',
    });

    if (selectedMode === 'saved') {
      goToSavedDatasetsStep();
      return;
    }

    goToDatasetStep();
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.header}>
          <Text
            as="h2"
            fontSize="lg"
            weight="semibold"
            className={styles.centerText}
          >
            Create Datasets
          </Text>
          <Text fontSize="md" className={styles.centerText}>
            Start with new datasets or continue working with ones you&apos;ve
            previously saved.
          </Text>
        </div>

        <DataBuilderGrid columns="2" className={styles.grid}>
          {START_OPTIONS.map((option) => {
            const isDisabled = option.id === 'saved' && isSavedDisabled;
            // `key` lives on whichever element this map callback returns —
            // either the bare <Option> or the wrapping <Tooltip>. Keeping it
            // off `optionEl` itself avoids two `key` props on the same render
            // path (React would only honor the outer one anyway).
            const optionEl = (
              <Option
                data-testid={option.testId}
                className={styles.optionCard}
                isSelected={selectedMode === option.id}
                disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) {
                    return;
                  }
                  setSelectedMode(option.id);
                }}
                startSlot={
                  <DataBuilderOption.Radio
                    isSelected={selectedMode === option.id}
                  />
                }
                startSlotAlignment="start"
                contentAlignment="start"
              >
                <div className={styles.optionBody}>
                  <Text weight="semibold">{option.title}</Text>
                  <Text fontSize="x-sm" className={styles.detail}>
                    {option.detail}
                  </Text>
                </div>
              </Option>
            );

            if (isDisabled) {
              return (
                <Tooltip
                  key={option.id}
                  label={NO_SAVED_DATASETS_TOOLTIP}
                  hasArrow
                  placement="top"
                  shouldWrapChildren
                >
                  {optionEl}
                </Tooltip>
              );
            }

            return <Fragment key={option.id}>{optionEl}</Fragment>;
          })}
        </DataBuilderGrid>

        <Button
          data-testid="step0-continue-button"
          color="primary"
          onClick={handleContinue}
          className={styles.continueButton}
        >
          Create Datasets
        </Button>
      </div>
    </section>
  );
};
